import { joinURL } from 'ufo'
import type { RouteLocationNormalized } from 'vue-router'
import { defineNuxtPlugin, markChunkRecovery, readReloadGuard, reloadNuxtApp, useRouter, useRuntimeConfig } from '#imports'

// Stale-chunk recovery. After a deploy, a client loaded against the previous build
// requests hashed chunk filenames that no longer exist and the dynamic import fails.
// Nuxt emits `app:chunkError` for every failed chunk import (navigation or preload);
// reload at the intended route to pull the current build. `reloadNuxtApp` carries a
// built-in 10s per-path guard, so a genuinely missing asset cannot cause a reload loop.

export default defineNuxtPlugin({
    name: 'chunk-reload',
    setup(nuxtApp) {
        const config = useRuntimeConfig()
        const router = useRouter()
        let currentlyNavigatingTo: RouteLocationNormalized | null = null

        router.beforeEach((to) => {
            currentlyNavigatingTo = to
        })
        router.afterEach(() => {
            currentlyNavigatingTo = null
        })

        // Reload when a `chunkError` is thrown
        nuxtApp.hook('app:chunkError', () => {
            const to = currentlyNavigatingTo ?? nuxtApp._route
            const target = joinURL(config.app.baseURL, to.fullPath)
            // A hash-only href change scrolls instead of reloading; drop the hash when the
            // rest of the URL already matches the current location.
            const targetWithoutHash = to.hash ? target.slice(0, -to.hash.length) : target
            const path = targetWithoutHash === window.location.pathname + window.location.search ? targetWithoutHash : target
            const guardBefore = readReloadGuard()
            reloadNuxtApp({ path, persistState: true })
            markChunkRecovery(guardBefore === false || readReloadGuard() !== guardBefore)
        })
    },
})
